import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TokenResponse } from '../interfaces/auth.interface';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  public accessToken$ = new BehaviorSubject<string>('');
  private tokenExpiryDate: Date | null = null;
  private readonly TOKEN_KEY = environment.auth.cookieKey;
  private readonly EXPIRY_DATE_KEY = environment.auth.cookieExpiryDateKey;
  private tokenExpirationTimer: any;
  private tokenRefresh$: Observable<any> | null = null;

  constructor(private http: HttpClient) {
    // Carga el token guardado al BehaviorSubject en el arranque.
    // Necesario para que isTokenValid() pueda verificarlo sin emitir en cada petición.
    const storedToken = localStorage.getItem(this.TOKEN_KEY);
    if (storedToken) {
      this.accessToken$.next(storedToken);
    }
  }

  // Method to obtain a new token from the API
  public fetchToken(): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('client_id', environment.auth.clientId);
    body.set('grant_type', environment.auth.grantType);
    body.set('scope', environment.auth.scope);
    body.set('client_secret', environment.auth.clientSecret);

    const tokenUrl = environment.URL_SECURITY_TOKEN;

    // const tokenUrl = `${environment.API_BASE_URL}${environment.auth.tokenEndpoint}`;

    return this.http.post<TokenResponse>(tokenUrl, body.toString(), { headers }).pipe(
      tap((response) => {
        this.accessToken$.next(response.access_token);
        const expiresInMilliseconds = response.expires_in * 1000;
        this.tokenExpiryDate = new Date(new Date().getTime() + expiresInMilliseconds);

        localStorage.setItem(this.TOKEN_KEY, response.access_token);
        localStorage.setItem(this.EXPIRY_DATE_KEY, this.tokenExpiryDate.toISOString());
        this.setTokenTimer(response.expires_in);
      }),
    );
  }

  /**
   * Inicializa el token de acceso.
   * - Token válido (>10 min): retorna inmediatamente sin emitir en el BehaviorSubject.
   * - Token expirado o ausente: lanza un único fetchToken() compartido entre
   *   todas las peticiones concurrentes (evita thundering herd).
   */
  public initializeToken(): Observable<any> {
    const storedToken = localStorage.getItem(this.TOKEN_KEY);
    const storedExpiryDate = localStorage.getItem(this.EXPIRY_DATE_KEY);

    if (storedToken && storedExpiryDate) {
      // Actualiza solo la fecha de expiración en memoria, sin disparar el BehaviorSubject
      this.tokenExpiryDate = new Date(storedExpiryDate);

      if (this.isTokenValid()) {
        // of() emite y completa en el mismo tick, sin efectos secundarios
        return of({ access_token: this.accessToken$.getValue() });
      }
    }

    // Token expirado o ausente: compartir el único refresh en vuelo
    return this.getOrStartRefresh();
  }

  /**
   * Retorna el observable de refresco en curso si ya existe,
   * o inicia uno nuevo. shareReplay(1) hace que todos los suscriptores
   * concurrentes reciban la misma respuesta sin lanzar múltiples peticiones.
   */
  private getOrStartRefresh(): Observable<any> {
    if (!this.tokenRefresh$) {
      this.tokenRefresh$ = this.fetchToken().pipe(
        finalize(() => { this.tokenRefresh$ = null; }),
        shareReplay(1),
      );
    }
    return this.tokenRefresh$;
  }

  // Method to get the existing token
  public getToken(): string {
    return this.accessToken$.getValue();
  }

  // Method to subscribe the existing token
  public subscribeToken(): BehaviorSubject<string> {
    return this.accessToken$;
  }

  // Helper method to check token validity
  private isTokenValid(): boolean {
    if (!this.accessToken$.getValue() || !this.tokenExpiryDate) {
      return false;
    }

    const currentDate = new Date();
    const timeDifference = (this.tokenExpiryDate.getTime() - currentDate.getTime()) / (1000 * 60);

    return timeDifference > 10; // Token is valid if it expires in more than 10 minutes
  }

  /**
   * Configura el temporizador para renovar el token
   */
  private setTokenTimer(expiresIn: number): void {
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }

    // Renovar el token antes de que expire según configuración
    this.tokenExpirationTimer = setTimeout(
      () => {
        this.fetchToken().subscribe();
      },
      (expiresIn - 60) * 1000,
    );
  }
}
